import type { AxiosError, AxiosResponse } from 'axios';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import {
  type GetContactPhoneShort,
  type HotSpotShort,
  type LiPimAttribute,
  type ProductInfoModelShort,
  type VwSparePartMultiComponentShort,
  type VwSparePartShort
} from '../api/hau-itde-spareparts-purchase-nonshop-portal-api';
import {
  assetApi,
  pimApi,
  relationApi,
  settingApi,
  sniApi,
  websiteConfigurationApi
} from '../api/hauItdeSparepartsPurchaseNonshopPortalApi';
import { useOrderStore } from './order';

export const useDataStore = defineStore('dataStore', () => {
  //-------State---------------------------------------------------
  const websiteConfigRecord = ref<Record<string, string>>({});
  const deviceDescription = ref('');
  const externalProductName = ref('');
  const serialnumber = ref('');
  const articleNumber = ref('');
  const serviceNumber = ref('');
  const isSearchMaskVisible = ref(true);
  const currentFileName = ref<string | null>(null);

  //cart
  const quantities = ref<Map<number, number>>(new Map());

  //general spareparts
  const fileNames = ref<Array<string>>([]);
  const hotspotsPositions = ref<{
    [key: string]: Array<HotSpotShort> | undefined;
  }>();
  const multiComponents = ref<{
    [key: string]: Array<VwSparePartMultiComponentShort> | undefined;
  }>();
  const isMultiCompontsLoaded = ref(false);

  //hotspots
  const hotspots = ref<Map<number, VwSparePartShort>>(new Map());
  const hotspotsPositionsPromise = ref<Promise<void>>();
  const isLoadingHotspot = ref(false);
  const isLoadingHotspots = ref(false);
  const allHotspotsLoaded = ref(false);
  const page = ref(1);
  const pageSize = ref(10);

  //filtered hotspots
  const isFilterActive = ref(false);
  const searchFilter = ref('');
  const filteredHotspots = ref<Map<number, VwSparePartShort>>(new Map());
  const filteredHotspotsPromise = ref<Promise<void>>();
  const isLoadingFilteredHotspots = ref(false);
  const allFilteredHotspotsLoaded = ref(false);
  const filteredPage = ref(1);
  const filteredPageSize = ref(10);

  //contact information
  const nationalPhone = ref('');
  const internationalPhone = ref('');

  //Store references
  const orderStore = useOrderStore();

  //---------MUTATATIONS------------------------------------------------------
  const incrementPage = () => {
    page.value += 1;
  };

  const incrementFilteredPage = () => {
    filteredPage.value += 1;
  };

  const setAllHotspotsLoaded = () => {
    allHotspotsLoaded.value = true;
  };

  const setAllFilteredHotspotsLoaded = () => {
    allFilteredHotspotsLoaded.value = true;
  };

  const setDeviceDescription = (_deviceDescription: string) => {
    deviceDescription.value = _deviceDescription;
    isSearchMaskVisible.value = false;
  };

  const setIsFilterActive = (_isFilterActive: boolean) => {
    isFilterActive.value = _isFilterActive;
  };

  const setSearchFilter = (_filter: string, _locale: string) => {
    searchFilter.value = _filter;

    if (!!searchFilter.value) {
      resetFilterAll();

      //load filtered hotspots initially based on the search filter
      loadFilteredHotSpots(
        serviceNumber.value,
        _locale,
        searchFilter.value,
        filteredPage.value,
        filteredPageSize.value
      );

      //switches data to filtered data in position table
      setIsFilterActive(true);
    } else {
      setIsFilterActive(false);
    }
  };

  const setExternalProductName = (_externalProductName: string) => {
    externalProductName.value = _externalProductName;
  };

  const setArticleNumber = (_articleNumber: string) => {
    articleNumber.value = _articleNumber;
  };

  const setServiceNumber = (_serviceNumber: string) => {
    serviceNumber.value = _serviceNumber;
  };

  const setSerialnumber = (_serialnumber: string) => {
    if (serialnumber.value.length >= 9) {
      //reset Store
      resetAll();
      //reset Orders
      const { resetOrders } = orderStore;
      resetOrders();
      //order Head gets currently keeped => likely same user across multiple serialnumber searches
    }
    serialnumber.value = _serialnumber;
  };

  const setFileNames = (_fileNames: Array<string>) => {
    fileNames.value = _fileNames;
  };

  const setHotSpots = (_hotSpots: Array<VwSparePartShort>) => {
    _hotSpots.forEach((_hotSpot) => {
      hotspots.value.set(_hotSpot.position!, _hotSpot);
    });
  };

  const setFilteredHotSpots = (_hotSpots: Array<VwSparePartShort>) => {
    _hotSpots.forEach((_hotSpot) => {
      filteredHotspots.value.set(_hotSpot.position!, _hotSpot);
    });
  };

  const setSingleHotSpot = (_hotspot: VwSparePartShort) => {
    hotspots.value.set(_hotspot.position!, _hotspot);
  };

  const setHotspotsPositions = (_hotspotsPositions: {
    [key: string]: Array<HotSpotShort> | undefined;
  }) => {
    hotspotsPositions.value = _hotspotsPositions;
  };

  const setMultiComponents = (_multiComponents: {
    [key: string]: Array<VwSparePartMultiComponentShort> | undefined;
  }) => {
    multiComponents.value = _multiComponents;
  };

  const setIsMultiComponentsLoaded = () => {
    isMultiCompontsLoaded.value = true;
  };

  const setCurrentFileName = (_fileName: string | null) => {
    currentFileName.value = _fileName;
  };

  const showSearchMask = () => {
    isSearchMaskVisible.value = true;
  };

  const setIsLoadingHotspot = (_isLoadingHotspot: boolean) => {
    isLoadingHotspot.value = _isLoadingHotspot;
  };

  const setIsLoadingHotspots = (_isLoadingHotspots: boolean) => {
    isLoadingHotspots.value = _isLoadingHotspots;
  };

  const setIsLoadingFilteredHotspots = (
    _isLoadingFilteredHotspots: boolean
  ) => {
    isLoadingFilteredHotspots.value = _isLoadingFilteredHotspots;
  };

  const setContactPhone = (_contactPhone: GetContactPhoneShort) => {
    nationalPhone.value = _contactPhone.nationalPhone || '';
    internationalPhone.value = _contactPhone.internationalPhone || '';
  };

  const setWebsiteConfig = (key: string, value: string) => {
    websiteConfigRecord.value[key] = value;
  };

  const resetAll = () => {
    deviceDescription.value = '';
    serialnumber.value = '';
    articleNumber.value = '';
    serviceNumber.value = '';
    isSearchMaskVisible.value = true;
    currentFileName.value = null;
    quantities.value = new Map();
    fileNames.value = [];
    isFilterActive.value = false;
    hotspotsPositions.value = undefined;
    multiComponents.value = undefined;
    isMultiCompontsLoaded.value = false;
    hotspots.value = new Map();
    hotspotsPositionsPromise.value = undefined;
    page.value = 1;
    pageSize.value = 10;
    isLoadingHotspot.value = false;
    isLoadingHotspots.value = false;
    allHotspotsLoaded.value = false;
    filteredHotspots.value = new Map();
    filteredHotspotsPromise.value = undefined;
    isLoadingFilteredHotspots.value = false;
    allFilteredHotspotsLoaded.value = false;
    filteredPage.value = 1;
    filteredPageSize.value = 10;
  };

  const resetFilterAll = () => {
    filteredHotspots.value = new Map();
    filteredHotspotsPromise.value = undefined;
    isLoadingFilteredHotspots.value = false;
    allFilteredHotspotsLoaded.value = false;
    filteredPage.value = 1;
    filteredPageSize.value = 10;
  };

  //-------ACTIONS----------------------------------------------------------
  const loadSerialnumberInfo = (serialnumber: string) => {
    return sniApi
      .v1SniArticledataSerialnumberGet(serialnumber)
      .then((res: AxiosResponse<ProductInfoModelShort>) => {
        if (res.status === 204) {
          throw new Error('Product Info not found');
        }

        //Set ArticleNumber
        if (!!res.data.salesArticleNo) {
          setArticleNumber(res.data.salesArticleNo);
        }

        //Set ServiceNumber
        if (!!res.data.serviceNumber) {
          setServiceNumber(res.data.serviceNumber);
        }

        //Set DeviceDescription
        if (!!res.data.externalDesignation) {
          setDeviceDescription(res.data.externalDesignation);
          return;
        }

        if (!!res.data.productDescription) {
          setDeviceDescription(res.data.productDescription);
          return;
        }

        if (!!res.data.description) {
          setDeviceDescription(res.data.description);
        }
      })
      .catch((err) => {
        //throw to catch error in frontend and set errorOccured
        throw err;
      });
  };

  //Longer version of deviceDescription => used on webshop appliances for example
  const loadExternalProductName = (articleNumber: string, locale: string) => {
    return pimApi
      .v1PimSkuAttributeIdGet(articleNumber, 5712, locale)
      .then((res: AxiosResponse<LiPimAttribute>) => {
        if (res.status === 204 || !res.data || !res.data.value) {
          throw new Error('External Product Name not found');
        }

        setExternalProductName(res.data.value);
      })
      .catch((err) => {
        throw err;
      });
  };

  const loadFileNames = (serviceNumber: string) => {
    return assetApi
      .v1AssetServiceNumberApplianceExplosiondrawingGet(serviceNumber)
      .then((res: AxiosResponse<Array<string>>) => {
        if (!res.data || (!!res.data && res.data.length === 0)) {
          throw new Error('No Files found for given serialnumber');
        }
        setFileNames(res.data);
      })
      .catch((err) => {
        //throw to catch error in frontend and set errorOccured
        throw err;
      });
  };

  const loadExplosionDrawing = (fileName: string) => {
    return assetApi
      .v1AssetExplosiondrawingFileNameGet(fileName)
      .then((res: AxiosResponse<string>) => {
        return res.data;
      })
      .catch((err) => {
        throw err;
      });
  };

  const loadSingleHotSpot = (
    serviceNumber: string,
    locale: string,
    position: number
  ) => {
    setIsLoadingHotspot(true);

    return relationApi
      .v1RelationGet(serviceNumber, locale, position)
      .then((res: AxiosResponse<VwSparePartShort>) => {
        if (res.status === 204) {
          throw new Error('SparePart not found');
        }

        setSingleHotSpot(res.data);
        setIsLoadingHotspot(false);
      })
      .catch((err: AxiosError) => {
        setIsLoadingHotspot(false);
        throw err;
      });
  };

  const loadHotspotsPositions = (
    serviceNumber: string,
    locale: string,
    fileNames: Array<string>
  ) => {
    hotspotsPositionsPromise.value = relationApi
      .v1RelationHotspotsPost(serviceNumber, locale, fileNames)
      .then(
        (
          res: AxiosResponse<{ [key: string]: Array<HotSpotShort> | undefined }>
        ) => {
          if (res.data === undefined) {
            throw new Error('No Hotspots found');
          }
          setHotspotsPositions(res.data);
        }
      )
      .catch((err) => {
        throw err;
      });

    return hotspotsPositionsPromise.value;
  };

  const loadHotSpots = (
    serviceNumber: string,
    locale: string,
    page: number,
    pageSize: number
  ) => {
    //check if filter is active => load hotspots by filter
    if (isFilterActive.value) {
      return loadFilteredHotSpots(
        serviceNumber,
        locale,
        searchFilter.value,
        page,
        pageSize
      );
    }

    //load hotspots without filter
    setIsLoadingHotspots(true);
    return relationApi
      .v1RelationPositionsGet(serviceNumber, locale, page, pageSize)
      .then((res: AxiosResponse<Array<VwSparePartShort>>) => {
        setHotSpots(res.data);
        incrementPage();
        setIsLoadingHotspots(false);

        if (res.data.length === 0) {
          setAllHotspotsLoaded();
        }
      })
      .catch((err) => {
        setIsLoadingHotspots(false);
        throw err;
      });
  };

  const loadFilteredHotSpots = (
    serviceNumber: string,
    locale: string,
    searchFilter: string,
    page: number,
    pageSize: number
  ) => {
    setIsLoadingFilteredHotspots(true);
    return relationApi
      .v1RelationPositionsFilterGet(
        serviceNumber,
        locale,
        searchFilter,
        page,
        pageSize
      )
      .then((res: AxiosResponse<Array<VwSparePartShort>>) => {
        //set Filtered Hot Spots
        setFilteredHotSpots(res.data);
        //set into main hotspots collection (better user experience)
        setHotSpots(res.data);

        incrementFilteredPage();
        setIsLoadingFilteredHotspots(false);

        if (res.data.length === 0) {
          setAllFilteredHotspotsLoaded();
        }
        setIsLoadingFilteredHotspots(false);
      })
      .catch((err) => {
        setIsLoadingFilteredHotspots(false);
        throw err;
      });
  };

  const loadMultiComponents = (serviceNumber: string, locale: string) => {
    return relationApi
      .v1RelationMulticomponentsGet(serviceNumber, locale)
      .then(
        (
          res: AxiosResponse<{
            [key: string]: Array<VwSparePartMultiComponentShort> | undefined;
          }>
        ) => {
          setMultiComponents(res.data);
          setIsMultiComponentsLoaded();
        }
      )
      .catch((err) => {
        throw err;
      });
  };

  const loadWebsiteConfigPage = (
    locale: string,
    pageId: string,
    baseUrl: string
  ) => {
    return websiteConfigurationApi
      .v1WebsiteconfigurationConfigurationGet(locale, pageId, baseUrl)
      .then((res: AxiosResponse<string>) => {
        setWebsiteConfig(pageId, res.data);
      })
      .catch((err) => {
        throw err;
      });
  };

  const loadContactPhone = (country: string) => {
    return settingApi
      .v1SettingPhoneGet(country.toUpperCase())
      .then((res: AxiosResponse<GetContactPhoneShort>) => {
        setContactPhone(res.data);
      })
      .catch((err) => {
        throw err;
      });
  };

  //--------------------Getters----------------------------------------------
  const getHotSpots = () => {
    return computed(() => {
      const hotspotMap = isFilterActive.value
        ? filteredHotspots.value
        : hotspots.value;

      /**
       * If filtered hotspots should not be visible and loaded via normal flow only,
       * return the hotspotMap as is and do not insert filtered hotspots into the main hotspots collection.
       * For more details, see loadFilteredHotSpots @see loadFilteredHotSpots and remove @see setHotSpots
       */
      return new Map(
        [...hotspotMap.entries()].sort(
          ([positionA], [positionB]) => positionA - positionB
        )
      );
    });
  };

  const getPage = () => {
    return computed(() => {
      if (isFilterActive.value) {
        return filteredPage.value;
      }

      return page.value;
    });
  };

  const getPageSize = () => {
    return computed(() => {
      if (isFilterActive.value) {
        return filteredPageSize.value;
      }
      return pageSize.value;
    });
  };

  const getHotSpotByPosition = (position: number) => {
    return computed(() => {
      return hotspots.value.get(position);
    });
  };

  const getHotspotsByFileName = (fileName: string) => {
    return computed(() => {
      if (hotspotsPositions.value && fileName in hotspotsPositions.value) {
        let hotspotsByFilenName: HotSpotShort[] | undefined =
          hotspotsPositions.value[fileName];
        return hotspotsByFilenName || [];
      }

      return [];
    });
  };

  const getZPartsByPosition = (position: number) => {
    return computed(() => {
      if (!multiComponents.value) {
        return [];
      }

      return (
        multiComponents.value[position.toString()]?.filter(
          (x) => x.articleGroup === 'ZPART'
        ) || []
      );
    });
  };

  const getVirtualGroupsByPosition = (position: number) => {
    return computed(() => {
      if (!multiComponents.value) {
        return [];
      }

      return (
        multiComponents.value[position.toString()]?.filter(
          (x) => x.articleGroup === 'VIRTUALGR' // > 1 levels <=> virtual group inside virtual group
        ) || []
      );
    });
  };

  const getVirtualElementsWithoutGroups = (position: number) => {
    const virtualGroup = getVirtualGroupsByPosition(position);
    let virtualGroupCopy: VwSparePartMultiComponentShort[] = [];

    const maxLevel = Math.max(...virtualGroup.value.map((x) => x.level!));
    if (maxLevel === 1) {
      return virtualGroup.value;
    }

    //add only real article into cart => remove virtual group nodes (if there are virtual groups inside virtual groups)
    virtualGroup.value.forEach((virtualElement) => {
      if (
        !virtualGroup.value.some(
          (x) =>
            x.level !== virtualElement.level &&
            x.articleNumberParent === virtualElement.articleNumber
        )
      ) {
        virtualGroupCopy.push(virtualElement);
      }
    });

    return virtualGroupCopy;
  };

  const getVirtualGroupsByPositionByLevel = (
    position: number,
    _level: number = 1
  ) => {
    return computed(() => {
      return getVirtualGroupsByPosition(position).value.filter(
        (x) => x.level === _level
      );
    });
  };

  const getVirtualGroupsByParent = (
    position: number,
    articleNumberParent: string
  ) => {
    return computed(() => {
      return getVirtualGroupsByPosition(position).value.filter(
        (x) => x.articleNumberParent === articleNumberParent
      );
    });
  };

  const checkDeepVirtualGroup = (position: number, _articleNumber: string) => {
    return computed(() => {
      return getVirtualGroupsByPosition(position).value.some(
        (x) => x.articleNumberParent === _articleNumber
      );
    });
  };

  const getWebsiteConfigPage = (pageId: string) => {
    return computed(() => {
      if (!(pageId in websiteConfigRecord.value)) {
        return '';
      }

      return websiteConfigRecord.value[pageId];
    });
  };

  const getIsLoadingHotspots = () => {
    return computed(() => {
      if (isFilterActive.value) {
        return isLoadingFilteredHotspots.value;
      }

      return isLoadingHotspots.value;
    });
  };

  const getAllHotspotsLoaded = () => {
    return computed(() => {
      if (isFilterActive.value) {
        return allFilteredHotspotsLoaded.value;
      }

      return allHotspotsLoaded.value;
    });
  };

  return {
    currentFileName,
    quantities,
    isSearchMaskVisible,
    serialnumber,
    multiComponents,
    isMultiCompontsLoaded,
    deviceDescription,
    externalProductName,
    fileNames,
    articleNumber,
    serviceNumber,
    searchFilter,
    page,
    pageSize,
    hotspots,
    hotspotsPositions,
    hotspotsPositionsPromise,
    isLoadingHotspot,
    isLoadingHotspots,
    isLoadingFilteredHotspots,
    allHotspotsLoaded,
    allFilteredHotspotsLoaded,
    nationalPhone,
    internationalPhone,
    isFilterActive,
    resetAll,
    setIsLoadingHotspot,
    loadSerialnumberInfo,
    loadFileNames,
    loadHotspotsPositions,
    loadSingleHotSpot,
    loadHotSpots,
    loadFilteredHotSpots,
    loadMultiComponents,
    loadContactPhone,
    loadWebsiteConfigPage,
    loadExternalProductName,
    setSearchFilter,
    setSerialnumber,
    setCurrentFileName,
    showSearchMask,
    loadExplosionDrawing,
    checkDeepVirtualGroup,
    getHotSpotByPosition,
    getHotspotsByFileName,
    getZPartsByPosition,
    getWebsiteConfigPage,
    getVirtualGroupsByPosition,
    getVirtualGroupsByPositionByLevel,
    getVirtualElementsWithoutGroups,
    getVirtualGroupsByParent,
    getHotSpots,
    getPage,
    getPageSize,
    getAllHotspotsLoaded,
    getIsLoadingHotspots
  };
});
